/*
 * TICK-140 — the six page templates (Figma `08 — App Shell & Templates`, node `38:4`).
 *
 * The brief's first architectural requirement is "do not implement 33 independent one-off page
 * layouts", and this file is where that is enforced rather than hoped for: a screen composes one of
 * these, and the template owns the arrangement, the state handling and the responsive behaviour.
 * What a screen supplies is its own data and controls.
 *
 * Each takes slots rather than data. A template that knew about orders could not also draw the
 * audit log, and a template generic enough to draw both would be a layout engine — slots keep the
 * arrangement shared and leave the domain where it already works.
 */
import type { ReactNode } from 'react';
import { DataTable, FilterToolbar, KpiGrid } from '../ui';
import type { Column, SortDirection } from '../ui';
import { TemplateGate, Card } from './TemplateGate';
import type { GateProps } from './TemplateGate';

type Gate = Omit<GateProps, 'children'>;

/* ── 1. Dashboard — KPI strip + what to do next beside a chart + a list ────────────────────── */
export function DashboardTemplate({ gate, kpis, actions, chart, list }: {
  gate: Gate;
  /** The metric strip. Four is the drawn width; the grid reflows rather than clipping a fifth. */
  kpis: ReactNode;
  /** What needs doing now. First, and beside the chart rather than under it — the chart is context
   *  for a decision, the action list *is* the decision. */
  actions?: ReactNode;
  chart?: ReactNode;
  list?: ReactNode;
}) {
  return (
    <TemplateGate {...gate}>
      <KpiGrid>{kpis}</KpiGrid>
      {(actions || chart) && (
        <div className="jt-dash-split">
          <div className="jt-section">{actions}</div>
          <div className="jt-section">{chart}</div>
        </div>
      )}
      {list}
    </TemplateGate>
  );
}

/* ── 2. Table / Queue — filters over a sortable, selectable, paged table ──────────────────── */
export function TableTemplate<T>({ gate, kpis, toolbar, summary, table, drawer }: {
  gate: Gate;
  /**
   * The metric strip some registries carry above their filters — a queue's backlog, a report's
   * totals. Optional, because most tables are a list and not a dashboard.
   */
  kpis?: ReactNode;
  /** Search, status chips and the screen's own filters. */
  toolbar?: ReactNode;
  /** A line above the table stating what is being counted — not a card, a sentence. */
  summary?: ReactNode;
  table: {
    label: string;
    columns: readonly Column<T>[];
    rows: readonly T[];
    rowKey: (row: T) => string;
    filtered?: boolean;
    onClearFilters?: () => void;
    emptyTitle?: string;
    emptyText?: ReactNode;
    sort?: { key: string; direction: SortDirection } | null;
    onSortChange?: (key: string, direction: SortDirection) => void;
    selected?: ReadonlySet<string>;
    onSelectedChange?: (next: Set<string>) => void;
    onRowClick?: (row: T) => void;
    activeRowKey?: string | null;
    /** The row as a record card, for phone width. */
    card?: (row: T) => ReactNode;
    footer?: ReactNode;
  };
  /** The row you opened, beside the list you opened it from. */
  drawer?: ReactNode;
}) {
  return (
    <TemplateGate {...gate}>
      {kpis && <KpiGrid>{kpis}</KpiGrid>}
      {toolbar && <FilterToolbar>{toolbar}</FilterToolbar>}
      {summary}
      <Card flush>
        <DataTable {...table} />
      </Card>
      {drawer}
    </TemplateGate>
  );
}

/* ── 3. Management — summary, toolbar, item cards, detail ─────────────────────────────────── */
export function ManagementTemplate({ gate, summary, toolbar, items, detail }: {
  gate: Gate;
  summary?: ReactNode;
  toolbar?: ReactNode;
  /** The collection, as cards or as a table — the screen decides which suits its rows. */
  items: ReactNode;
  detail?: ReactNode;
}) {
  return (
    <TemplateGate {...gate}>
      {summary}
      {toolbar && <FilterToolbar>{toolbar}</FilterToolbar>}
      {items}
      {detail}
    </TemplateGate>
  );
}

/* ── 4. Editor / Form — grouped fields, a preview, and a sticky save rail ─────────────────── */
export function EditorTemplate({ gate, kpis, sections, preview, stickyActions }: {
  gate: Gate;
  /**
   * The strip some editors carry above the form — what the settings on this page add up to.
   * Optional and full width, above the rail rather than inside the form column, because it
   * describes both halves.
   */
  kpis?: ReactNode;
  /** Grouped field sets. Each is a `Card`, so the form has headings rather than one long column. */
  sections: ReactNode;
  /** What the change will look like, where the screen can show it. */
  preview?: ReactNode;
  /** The save rail. Sticky, so it is reachable from anywhere in a long form and at any width. */
  stickyActions?: ReactNode;
}) {
  return (
    <TemplateGate {...gate}>
      {kpis && <KpiGrid>{kpis}</KpiGrid>}
      {preview ? (
        <div className="jt-with-rail">
          <div className="jt-form-section">{sections}</div>
          <div className="jt-section">{preview}</div>
        </div>
      ) : (
        <div className="jt-form-section">{sections}</div>
      )}
      {stickyActions}
    </TemplateGate>
  );
}

/* ── 5. Operations — one focused control, unmistakable feedback, then the record ──────────── */
export function OperationsTemplate({ gate, kpis, focus, result, context }: {
  gate: Gate;
  /** What the operation has achieved so far. Optional, and above everything else when present. */
  kpis?: ReactNode;
  /** The control the screen exists for — the scanner, the canvas, the queue board. */
  focus: ReactNode;
  /**
   * The outcome of the last action, in the largest type on the page.
   *
   * It sits above the context rather than below it because during a live operation nobody scrolls:
   * the answer to "did that work" has to be where the eye already is.
   */
  result?: ReactNode;
  context?: ReactNode;
}) {
  return (
    <TemplateGate {...gate}>
      {kpis && <KpiGrid>{kpis}</KpiGrid>}
      {result}
      {focus}
      {context}
    </TemplateGate>
  );
}

/* ── 6. Safety / Integration — health, configuration, consequence, audit ──────────────────── */
export function SafetyTemplate({ gate, health, configuration, consequence, audit, dangerZone }: {
  gate: Gate;
  /** Whether the thing is working right now, before any control to change it. */
  health: ReactNode;
  configuration?: ReactNode;
  /**
   * What turning this off does to people mid-purchase, stated next to the switch rather than in the
   * dialog that follows it. A kill switch whose cost is only revealed after you press it is a kill
   * switch that gets pressed to find out.
   */
  consequence?: ReactNode;
  audit?: ReactNode;
  dangerZone?: ReactNode;
}) {
  return (
    <TemplateGate {...gate}>
      {health}
      {consequence}
      {configuration}
      {audit}
      {dangerZone}
    </TemplateGate>
  );
}

export { TemplateGate, Card } from './TemplateGate';
